import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	FormControl,
	FormHelperText,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { changeUserPassword } from 'store/user-service/asyncActions';
import { getIsLoading } from 'store/user-service/selector';

import { NamespacesList } from 'core/enums/common';
import { userChangePassword } from 'core/helpers/yupSchemas';

import { SubmitButton } from 'components/Profile/styled';
import { SharedModal } from 'components/shared/Modal';

import { ReactComponent as EyeSlash } from 'assets/icons/eye-slash.svg';
import { ReactComponent as Lock } from 'assets/icons/lock.svg';

import * as Styled from './styled';

interface ChangePasswordModalProps {
	open: boolean;
	onClose: () => void;
}

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({ open, onClose }) => {
	const dispatch = useAppDispatch();

	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);
	const { t: changePasswordTranslate } = useTranslation(NamespacesList['changePassword']);

	const [showPassword, setShowPassword] = useState(false);

	const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : '';

	const isLoading = useSelector(getIsLoading);

	const {
		handleSubmit,
		register,
		formState: { errors, isDirty },
		reset,
	} = useForm({
		resolver: yupResolver(userChangePassword()),
	});

	const handleClose = () => {
		onClose();
		reset();
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleChangePassword = (data: {
		oldPassword: string;
		newPassword: string;
		confirmNewPassword: string;
	}) => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { confirmNewPassword, ...transferredData } = data;
		dispatch(
			changeUserPassword({
				id: user.id,
				data: transferredData,
				function: handleClose,
			})
		);
	};

	return (
		<SharedModal isOpen={open} handleClose={handleClose}>
			<Styled.ChangePasswordModalContent>
				<Lock />
				<Styled.Title>{modalTranslate('changePasswordModal.title')}</Styled.Title>
				<form onSubmit={handleSubmit(handleChangePassword)} style={{ width: '100%' }}>
					<Styled.Form className="rtl-able">
						<FormControl fullWidth error={!!errors.oldPassword}>
							<Typography sx={{ fontSize: '12px' }}>
								{changePasswordTranslate('text.oldPassword')}
							</Typography>
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Button
												disableRipple
												sx={{
													minWidth: 0,
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.1)',
													},
												}}
												onClick={handleClickShowPassword}
											>
												<EyeSlash />
											</Button>
										</InputAdornment>
									),
								}}
								inputProps={{
									style: {
										padding: '10px 12px',
									},
								}}
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
								autoComplete="password"
								type={showPassword ? 'text' : 'password'}
								placeholder={changePasswordTranslate('placeholders.password') as string}
								{...register('oldPassword')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.oldPassword?.message}
							</FormHelperText>
						</FormControl>
						<FormControl fullWidth error={!!errors.newPassword}>
							<Typography sx={{ fontSize: '12px' }}>
								{changePasswordTranslate('text.newPassword')}
							</Typography>
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Button
												disableRipple
												sx={{
													minWidth: 0,
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.1)',
													},
												}}
												onClick={handleClickShowPassword}
											>
												<EyeSlash />
											</Button>
										</InputAdornment>
									),
								}}
								inputProps={{
									style: {
										padding: '10px 12px',
									},
								}}
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
								autoComplete="password"
								type={showPassword ? 'text' : 'password'}
								placeholder={changePasswordTranslate('placeholders.password') as string}
								{...register('newPassword')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.newPassword?.message}
							</FormHelperText>
						</FormControl>
						<FormControl fullWidth error={!!errors.confirmNewPassword}>
							<Typography sx={{ fontSize: '12px' }}>
								{changePasswordTranslate('text.confirmNewPassword')}
							</Typography>
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Button
												disableRipple
												sx={{
													minWidth: 0,
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.1)',
													},
												}}
												onClick={handleClickShowPassword}
											>
												<EyeSlash />
											</Button>
										</InputAdornment>
									),
								}}
								inputProps={{
									style: {
										padding: '10px 12px',
									},
								}}
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
								autoComplete="password"
								type={showPassword ? 'text' : 'password'}
								placeholder={changePasswordTranslate('placeholders.password') as string}
								{...register('confirmNewPassword')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.confirmNewPassword?.message}
							</FormHelperText>
						</FormControl>
						{isLoading ? (
							<SubmitButton variant="contained" color="primary" disabled={isLoading}>
								<Oval
									height={24}
									width={24}
									color="#fff"
									wrapperStyle={{}}
									wrapperClass=""
									visible={true}
									ariaLabel="oval-loading"
									secondaryColor="#91B6FF"
									strokeWidth={5}
									strokeWidthSecondary={5}
								/>
							</SubmitButton>
						) : (
							<SubmitButton
								type="submit"
								variant="contained"
								color="primary"
								// disabled
								disabled={!isDirty}
							>
								{modalTranslate('changePasswordModal.title')}
							</SubmitButton>
						)}
					</Styled.Form>
				</form>
			</Styled.ChangePasswordModalContent>
		</SharedModal>
	);
};
